import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Brugerens eget game data
  currentPhase: 0,
  preseasonWinner: '',
  leagueStanding: [],
  playoffPredictions: {},
  knockoutPredictions: {},
  finalPrediction: '',
  points: 0,
  gameStarted: false,
  realResults: {},
  phaseLocks: {
    phase0: false,
    phase1: false,
    phase2: false,
    phase2Playoff: false,
    phase2Round16: false,
    phase2Quarters: false,
    phase2Semis: false,
    phase3: false,
  },
  // Alle spilleres game data (til leaderboard)
  allGameData: [],
  allGameDataLoading: false,
  allGameDataError: '',
  // Settings data
  settingsData: {},
  settingsLoading: false,
  settingsError: '',
};

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setCurrentPhase: (state, action) => {
      state.currentPhase = action.payload;
    },
    setPreseasonWinner: (state, action) => {
      state.preseasonWinner = action.payload;
    },
    setLeagueStanding: (state, action) => {
      state.leagueStanding = action.payload;
    },
    setPlayoffPredictions: (state, action) => {
      state.playoffPredictions = action.payload;
    },
    setKnockoutPredictions: (state, action) => {
      state.knockoutPredictions = action.payload;
    },
    setFinalPrediction: (state, action) => {
      state.finalPrediction = action.payload;
    },
    setPoints: (state, action) => {
      state.points = action.payload;
    },
    setGameStarted: (state, action) => {
      state.gameStarted = action.payload;
    },
    setRealResults: (state, action) => {
      state.realResults = action.payload;
    },
    setPhaseLocks: (state, action) => {
      state.phaseLocks = action.payload;
    },
    startGame: (state) => {
      state.gameStarted = true;
      state.currentPhase = 0;
    },
    nextPhase: (state) => {
      if (state.currentPhase < 3) {
        const nextPhaseNum = state.currentPhase + 1;
        const phaseKey = `phase${nextPhaseNum}`;
        if (!state.phaseLocks[phaseKey]) {
          state.currentPhase = nextPhaseNum;
        }
      }
    },
    prevPhase: (state) => {
      if (state.currentPhase > 0) {
        const prevPhaseNum = state.currentPhase - 1;
        const phaseKey = `phase${prevPhaseNum}`;
        if (!state.phaseLocks[phaseKey]) {
          state.currentPhase = prevPhaseNum;
        }
      }
    },
    resetGame: (state) => {
      return {
        ...initialState,
        realResults: state.realResults, // Behold real results
        phaseLocks: state.phaseLocks, // Behold phase locks
      };
    },
    loadGameFromStorage: (state) => {
      const savedGame = localStorage.getItem('championsLeagueGame');
      if (savedGame) {
        const gameData = JSON.parse(savedGame);
        state.preseasonWinner = gameData.preseasonWinner || '';
        state.leagueStanding = gameData.leagueStanding || [];
        state.playoffPredictions = gameData.playoffPredictions || {};
        state.knockoutPredictions = gameData.knockoutPredictions || {};
        state.finalPrediction = gameData.finalPrediction || '';
        state.points = gameData.points || 0;
        state.gameStarted = gameData.gameStarted || false;
        state.currentPhase = gameData.currentPhase || 0;
      }

      const savedRealResults = localStorage.getItem('championsLeagueRealResults');
      if (savedRealResults) {
        state.realResults = JSON.parse(savedRealResults);
      }

      const savedPhaseLocks = localStorage.getItem('championsLeaguePhaseLocks');
      if (savedPhaseLocks) {
        state.phaseLocks = JSON.parse(savedPhaseLocks);
      }
    },
    saveGameToStorage: (state) => {
      const gameData = {
        preseasonWinner: state.preseasonWinner,
        leagueStanding: state.leagueStanding,
        playoffPredictions: state.playoffPredictions,
        knockoutPredictions: state.knockoutPredictions,
        finalPrediction: state.finalPrediction,
        points: state.points,
        gameStarted: state.gameStarted,
        currentPhase: state.currentPhase,
      };
      localStorage.setItem('championsLeagueGame', JSON.stringify(gameData));
    },
    // Actions til at håndtere alle spilleres game data
    setAllGameDataLoading: (state, action) => {
      state.allGameDataLoading = action.payload;
    },
    setAllGameData: (state, action) => {
      state.allGameData = action.payload;
      state.allGameDataLoading = false;
      state.allGameDataError = '';
    },
    setAllGameDataError: (state, action) => {
      state.allGameDataError = action.payload;
      state.allGameDataLoading = false;
    },
    // Actions til at håndtere settings data
    setSettingsDataLoading: (state, action) => {
      state.settingsLoading = action.payload;
    },
    setSettingsData: (state, action) => {
      state.settingsData = action.payload;
      state.settingsLoading = false;
      state.settingsError = '';
      // Opdater også phaseLocks hvis de findes i settings
      if (action.payload?.phaseLocks) {
        state.phaseLocks = action.payload.phaseLocks;
      }
    },
    setSettingsDataError: (state, action) => {
      state.settingsError = action.payload;
      state.settingsLoading = false;
    },
  },
});

export const {
  setCurrentPhase,
  setPreseasonWinner,
  setLeagueStanding,
  setPlayoffPredictions,
  setKnockoutPredictions,
  setFinalPrediction,
  setPoints,
  setGameStarted,
  setRealResults,
  setPhaseLocks,
  startGame,
  nextPhase,
  prevPhase,
  resetGame,
  loadGameFromStorage,
  saveGameToStorage,
  setAllGameDataLoading,
  setAllGameData,
  setAllGameDataError,
  setSettingsDataLoading,
  setSettingsData,
  setSettingsDataError,
} = gameSlice.actions;

export default gameSlice.reducer;
